import { useRef, useState, useEffect } from "react";
import { ChartCanvas, Chart } from "@react-financial-charts/core";
import { XAxis, YAxis } from "@react-financial-charts/axes";
import { CandlestickSeries, LineSeries } from "@react-financial-charts/series";
import { discontinuousTimeScaleProviderBuilder } from "@react-financial-charts/scales";
import { CrossHairCursor, MouseCoordinateX, MouseCoordinateY } from "@react-financial-charts/coordinates";
import { OHLCTooltip, SingleValueTooltip } from "@react-financial-charts/tooltip";
import { Annotate, LabelAnnotation } from "@react-financial-charts/annotations";
import { format } from "d3-format";
import { timeFormat } from "d3-time-format";
import type { CaMarkerDto, EarningsMarkerDto, InstrumentBar, MacroMarkerDto } from "../api";

/** react-financial-charts is Gate-0's mandated production charting library.
 * Every series is REAL data: candles from `bars`, every overlay/sub-panel
 * line from the on-demand TA-Lib indicator engine (docs/technical_
 * indicators_strategy.md's Tier-3 plan -- app/api/routes/indicators.py) --
 * nothing computed twice, nothing hardcoded to a fixed few indicators. The
 * caller (InstrumentDetailPage) owns which indicators are active and fetches
 * their series; this component only renders whatever it's given. There is
 * no forecast/projection cone: the mock's dashed drift line + σ cone needs
 * path-level simulation output that doesn't exist yet (ui_spec #9, Phase 7
 * status: not built) -- omitted rather than faked. */

export interface ActiveIndicatorSeries {
  key: string; // unique per (name + params) -- e.g. "SMA:5" vs "SMA:10"
  displayName: string;
  color: string;
  overlay: boolean;
  outputNames: string[];
  seriesByDate: Map<string, Record<string, number | null>>;
}

interface ChartDatum {
  date: Date;
  dateKey: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number | null;
  ind: Record<string, number | null>; // "<indicatorKey>.<outputName>" -> value
}

const SUBPANEL_HEIGHT = 90;
const PRICE_HEIGHT = 260;

/** Each output line of one indicator gets its own colour, so a multi-line
 * indicator (MACD, BBANDS, SMI, Stochastic) is readable at a glance. The
 * first output keeps the indicator's own colour; the rest draw from a
 * palette chosen to stay distinct from one another. */
const OUTPUT_PALETTE = ["#2563eb", "#dc2626", "#16a34a", "#d97706", "#9333ea", "#0891b2"];

function outputColor(indicatorColor: string, index: number): string {
  return index === 0 ? indicatorColor : OUTPUT_PALETTE[(index - 1) % OUTPUT_PALETTE.length];
}

function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(Math.floor(w));
    });
    ro.observe(el);
    setWidth(Math.floor(el.clientWidth));
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

export function PriceChart({
  bars,
  caMarkers,
  earningsMarkers,
  macroMarkers,
  indicators,
}: {
  bars: InstrumentBar[];
  caMarkers: CaMarkerDto[];
  earningsMarkers: EarningsMarkerDto[];
  macroMarkers: MacroMarkerDto[];
  indicators: ActiveIndicatorSeries[];
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  const usableBars = bars.filter((b) => b.open !== null && b.high !== null && b.low !== null && b.close !== null);
  const caByDate = new Map(caMarkers.map((m) => [m.ex_date, m]));
  const earningsByDate = new Set(earningsMarkers.map((m) => m.announce_date));
  const macroByDate = new Map(macroMarkers.map((m) => [m.event_date, m]));

  const data: ChartDatum[] = usableBars.map((b) => {
    const ind: Record<string, number | null> = {};
    for (const s of indicators) {
      const vals = s.seriesByDate.get(b.date);
      for (const out of s.outputNames) {
        ind[`${s.key}.${out}`] = vals ? (vals[out] ?? null) : null;
      }
    }
    return {
      date: new Date(b.date), dateKey: b.date,
      open: b.open as number, high: b.high as number, low: b.low as number, close: b.close as number,
      volume: b.volume, ind,
    };
  });

  if (data.length < 2 || width === 0) {
    return (
      <div ref={ref} style={{ minHeight: 320 }}>
        {data.length < 2 ? <div className="empty-state">Not enough bars in this window to chart.</div> : null}
      </div>
    );
  }

  const scaleProvider = discontinuousTimeScaleProviderBuilder().inputDateAccessor((d: ChartDatum) => d.date);
  const { data: plotData, xScale, xAccessor, displayXAccessor } = scaleProvider(data);
  const xExtents = [xAccessor(plotData[Math.max(0, plotData.length - 120)]), xAccessor(plotData[plotData.length - 1])];

  // The discontinuous (weekend-skipping) time scale is INDEX-based -- tick
  // values XAxis/MouseCoordinateX hand to a formatter are row indices into
  // `plotData`, not real dates. Passing a raw d3 timeFormat straight through
  // (as if the tick were a Date) formats "ms since epoch" for a tiny index
  // -- every tick reads "01 Jan". Map index -> real date first.
  const formatTickDate = (fmt: string) => {
    const f = timeFormat(fmt);
    return (index: number) => {
      const d = plotData[Math.round(index)];
      return d ? f(d.date) : "";
    };
  };

  const eventLabel = (d: ChartDatum): string | null => {
    const ca = caByDate.get(d.dateKey);
    if (ca) return `CA ${ca.status}${ca.observed_ratio ? ` ${ca.observed_ratio.toFixed(2)}x` : ""}`;
    if (earningsByDate.has(d.dateKey)) return "Earnings";
    const macro = macroByDate.get(d.dateKey);
    if (macro) return macro.event_type ?? "Macro";
    return null;
  };

  const overlays = indicators.filter((s) => s.overlay);
  const subPanels = indicators.filter((s) => !s.overlay);
  const totalHeight = PRICE_HEIGHT + subPanels.length * SUBPANEL_HEIGHT;

  // The chart's own x-axis is monthly-gridlined (readable trend, but the
  // last real candle can sit well past the last labeled tick) -- a plain,
  // always-visible "last bar" date removes the ambiguity without needing a
  // hover, independent of the chart library's own SVG/canvas layout.
  const lastBarDate = timeFormat("%d %b %Y")(plotData[plotData.length - 1].date);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={{
          position: "absolute", top: 4, right: 60, zIndex: 1,
          fontFamily: "IBM Plex Mono, monospace", fontSize: "0.72rem", color: "#96a0aa",
        }}
      >
        last bar: {lastBarDate}
      </div>
      {indicators.some((s) => s.outputNames.length > 1) && (
        <div
          style={{
            display: "flex", flexWrap: "wrap", gap: "4px 14px", padding: "2px 8px 6px",
            fontFamily: "IBM Plex Mono, monospace", fontSize: "0.72rem",
          }}
        >
          {indicators.flatMap((s) =>
            s.outputNames.length > 1
              ? s.outputNames.map((out, j) => (
                  <span key={`legend-${s.key}.${out}`} style={{ color: outputColor(s.color, j), whiteSpace: "nowrap" }}>
                    ━ {s.displayName.split(" —")[0]} · {out}
                  </span>
                ))
              : [],
          )}
        </div>
      )}
      <ChartCanvas
        height={totalHeight}
        width={width}
        ratio={window.devicePixelRatio || 1}
        margin={{ left: 56, right: 56, top: 12, bottom: 24 }}
        data={plotData}
        seriesName="price"
        xScale={xScale}
        xAccessor={xAccessor}
        displayXAccessor={displayXAccessor}
        xExtents={xExtents}
      >
        <Chart id={0} height={PRICE_HEIGHT} yExtents={(d: ChartDatum) => [d.high, d.low]} padding={{ top: 10, bottom: 10 }}>
          <XAxis showGridLines tickFormat={formatTickDate("%d %b")} />
          <YAxis showGridLines tickFormat={format(".2f")} />
          <CandlestickSeries fill={(d: ChartDatum) => (d.close >= d.open ? "#0e9f6e" : "#df453b")} />
          {overlays.flatMap((s) =>
            s.outputNames.map((out, j) => (
              <LineSeries
                key={`${s.key}.${out}`}
                yAccessor={(d: ChartDatum) => d.ind[`${s.key}.${out}`] ?? undefined}
                strokeStyle={outputColor(s.color, j)}
                strokeWidth={1.3}
              />
            )),
          )}
          <Annotate
            with={LabelAnnotation}
            when={(d: ChartDatum) => eventLabel(d) !== null}
            usingProps={{
              y: (props: { yScale: (v: number) => number }) => props.yScale(0) - 8,
              text: (d: ChartDatum) => eventLabel(d) ?? "",
              fontFamily: "IBM Plex Mono",
              fontSize: 9,
              fill: "#c97a1f",
              rotate: -90,
              textAnchor: "start",
            }}
          />
          <MouseCoordinateY displayFormat={format(".2f")} />
          {/* Date readout for the hovered candle -- OHLCTooltip has no date
              slot of its own (checked its props: o/h/l/c/na labels only), so
              this is a second, deliberately-adjacent tooltip rather than a
              fork of OHLCTooltip's rendering. */}
          <SingleValueTooltip
            origin={[8, 4]}
            yLabel="Date"
            yAccessor={(d: ChartDatum) => d.date as unknown as number}
            yDisplayFormat={((d: Date) => timeFormat("%d %b %Y")(d)) as unknown as (n: number) => string}
          />
          <OHLCTooltip origin={[8, 20]} />
          {subPanels.length === 0 && <MouseCoordinateX displayFormat={formatTickDate("%d %b %Y")} />}
        </Chart>
        {subPanels.map((s, i) => {
          const keys = s.outputNames.map((out) => `${s.key}.${out}`);
          const isLast = i === subPanels.length - 1;
          return (
            <Chart
              key={s.key}
              id={i + 1}
              height={SUBPANEL_HEIGHT}
              yExtents={(d: ChartDatum) => keys.map((k) => d.ind[k]).filter((v): v is number => v !== null && v !== undefined)}
              origin={(_w: number, h: number) => [0, h - (subPanels.length - i) * SUBPANEL_HEIGHT]}
            >
              <XAxis showGridLines={isLast} tickFormat={formatTickDate("%d %b")} />
              <YAxis tickFormat={format(".1f")} />
              {keys.map((k, j) => (
                <LineSeries
                  key={k}
                  yAccessor={(d: ChartDatum) => d.ind[k] ?? undefined}
                  strokeStyle={outputColor(s.color, j)}
                  strokeWidth={1.3}
                />
              ))}
              <MouseCoordinateY displayFormat={format(".2f")} />
              {isLast && <MouseCoordinateX displayFormat={formatTickDate("%d %b %Y")} />}
            </Chart>
          );
        })}
        <CrossHairCursor />
      </ChartCanvas>
    </div>
  );
}
